import { Button, ListItem, ListItemIcon, ListItemText } from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import Container from "@material-ui/core/Container";
import CssBaseline from "@material-ui/core/CssBaseline";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import { makeStyles } from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import BarChartIcon from "@material-ui/icons/BarChart";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import CorpOriginal from "@material-ui/icons/CropOriginal";
import DashboardIcon from "@material-ui/icons/Dashboard";
import MenuIcon from "@material-ui/icons/Menu";
import OfflineBolt from "@material-ui/icons/OfflineBolt";
import PeopleIcon from "@material-ui/icons/People";
import clsx from "clsx";
import { useRpcDispatcher } from "cooke-rpc-react";
import React from "react";
import { Redirect, Route, Switch } from "react-router";
import { useAppContext } from "./context";
import { sessionController } from "./generated/rpc";
import { OrdersTable } from "./Orders";
import { OrganizationView } from "./Organization";
import { OrganizationsTable } from "./Organizations";
import { routes } from "./routes";
import { SessionsTable } from "./Sessions";
import { UsersTable } from "./Users";
import { UserView } from "./UserView";
import { RouterLink } from "./utils";
import { WordsTable } from "./Words";
import { WordView } from "./WordView";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    ...theme.mixins.toolbar,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: 36,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    flexGrow: 1,
    height: "100vh",
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  fixedHeight: {
    height: 240,
  },
  activeMenuItem: {},
}));

export default function LoggedInPage() {
  const classes = useStyles();
  const [open, setOpen] = React.useState(true);
  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };

  const [, setAppContext] = useAppContext();
  const rpcDispatcher = useRpcDispatcher();

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="absolute" className={clsx(classes.appBar, open && classes.appBarShift)}>
        <Toolbar className={classes.toolbar}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            className={clsx(classes.menuButton, open && classes.menuButtonHidden)}
          >
            <MenuIcon />
          </IconButton>
          <Typography component="h1" variant="h6" color="inherit" noWrap className={classes.title}>
            SimpliOrd Administration
          </Typography>
          <Button
            color="inherit"
            onClick={() => {
              rpcDispatcher(sessionController.logout());
              setAppContext((p) => ({ ...p, session: null }));
            }}
          >
            Logga ut
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        classes={{
          paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
        }}
        open={open}
      >
        <div className={classes.toolbarIcon}>
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List>
          <div>
            <ListItem button component={RouterLink} to={routes.words.path}>
              <ListItemIcon>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Ord" />
            </ListItem>
            <ListItem button component={RouterLink} to={routes.users}>
              <ListItemIcon>
                <PeopleIcon />
              </ListItemIcon>
              <ListItemText primary="Användare" />
            </ListItem>
            <ListItem button component={RouterLink} to={routes.orders}>
              <ListItemIcon>
                <BarChartIcon />
              </ListItemIcon>
              <ListItemText primary="Ordrar" />
            </ListItem>
            <ListItem button component={RouterLink} to={routes.sessions}>
              <ListItemIcon>
                <OfflineBolt />
              </ListItemIcon>
              <ListItemText primary="Sessioner" />
            </ListItem>
            <ListItem button component={RouterLink} to={routes.organizations}>
              <ListItemIcon>
                <CorpOriginal />
              </ListItemIcon>
              <ListItemText primary="Organisationer" />
            </ListItem>
          </div>
        </List>
      </Drawer>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        <Container maxWidth="lg" className={classes.container}>
          <Switch>
            <Route exact path={routes.words.path}>
              <WordsTable></WordsTable>
            </Route>

            <Route exact path={routes.word.path}>
              <WordView />
            </Route>

            <Route exact path={routes.users}>
              <UsersTable />
            </Route>

            <Route exact path={routes.user}>
              <UserView />
            </Route>

            <Route exact path={routes.orders}>
              <OrdersTable />
            </Route>

            <Route exact path={routes.sessions}>
              <SessionsTable />
            </Route>

            <Route exact path={routes.organizations}>
              <OrganizationsTable />
            </Route>

            <Route exact path={routes.organization}>
              <OrganizationView />
            </Route>

            <Route path="/">
              <Redirect to={routes.words.path}></Redirect>
            </Route>
          </Switch>
        </Container>
      </main>
    </div>
  );
}
